/**
 * CodeField — the 6-digit code every emailed-code step asks for (sign-in,
 * sign-up, a new device, an email change).
 *
 * Six separate boxes over ONE real input (`input-otp`): the phone's and the
 * browser's one-time-code autofill fill all six, paste works, and a test can
 * `fill()` it. Digits only. `onComplete` fires once all six are in, so the
 * step can go on without a click.
 *
 * A whole code pasted over the field REPLACES what is there. `input-otp`
 * inserts a paste at the selection, which on a full field is the last box —
 * so the right code pasted after a wrong one went out as `000004`.
 */

import { REGEXP_ONLY_DIGITS } from 'input-otp';

import { cn } from '../lib/utils';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../primitives/input-otp';

export const CODE_LENGTH = 6;

const REGEXP_WHOLE_CODE = new RegExp(REGEXP_ONLY_DIGITS);

export interface CodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  'aria-describedby'?: string;
  'data-testid'?: string;
  className?: string;
}

export function CodeField({
  value,
  onChange,
  onComplete,
  id,
  disabled,
  invalid,
  autoFocus,
  className,
  ...rest
}: CodeFieldProps) {
  return (
    <InputOTP
      id={id}
      maxLength={CODE_LENGTH}
      pattern={REGEXP_ONLY_DIGITS}
      inputMode="numeric"
      autoComplete="one-time-code"
      value={value}
      onChange={onChange}
      onComplete={onComplete}
      onPaste={(event) => {
        const pasted = event.clipboardData.getData('text/plain').replace(/\s/g, '');
        if (pasted.length !== CODE_LENGTH || !REGEXP_WHOLE_CODE.test(pasted)) return;
        event.preventDefault();
        onChange(pasted);
        // `input-otp` fires `onComplete` only when the value BECOMES full; a
        // full field replaced by another full code is ours to announce.
        if (value.length === CODE_LENGTH) onComplete?.(pasted);
      }}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-invalid={invalid || undefined}
      aria-describedby={rest['aria-describedby']}
      data-testid={rest['data-testid']}
      containerClassName={cn('w-full', className)}
    >
      <InputOTPGroup className="grid w-full grid-cols-6 gap-2">
        {Array.from({ length: CODE_LENGTH }, (_, index) => (
          <InputOTPSlot
            key={index}
            index={index}
            aria-invalid={invalid || undefined}
            className="h-14 w-full rounded-[10px] border border-input bg-card text-2xl font-semibold first:rounded-[10px] last:rounded-[10px]"
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}
